import CWButton, {
  IButtonProps,
} from '@/context/global/components/g00/g00-d00-g006-cw-button';
import { cn } from '@/context/global/utils/cn';
import React from 'react';

export interface ICWPopupProps {
  title: string;
  leftButtonText?: string;
  rightButtonText?: string;
  onLeftButtonClick?: () => void;
  onRightButtonClick?: () => void;
  onClose?: () => void;
  isOpen?: boolean;
  className?: string;
  leftButtonProps?: Omit<IButtonProps, 'title' | 'onClick'>;
  rightButtonProps?: Omit<IButtonProps, 'title' | 'onClick'>;
  topElement?: React.ReactNode;
  hideCloseButton?: boolean;
}

const CWPopup = ({
  title,
  leftButtonText,
  rightButtonText,
  onLeftButtonClick,
  onRightButtonClick,
  onClose,
  isOpen = true,
  className,
  leftButtonProps,
  rightButtonProps,
  topElement,
  hideCloseButton = false,
}: ICWPopupProps) => {
  if (!isOpen) return null;
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose?.();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 md:px-0"
      onClick={handleOverlayClick}
    >
      <div
        className={cn(
          'z-60 relative flex flex-col items-center justify-center gap-6 rounded-[20px] bg-[#161719] p-10 md:gap-10 md:p-[50px]',
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        {!hideCloseButton && (
          <button
            className="absolute right-4 top-4 flex h-8 w-8 cursor-pointer items-center justify-center text-white transition-opacity hover:text-ais-green"
            onClick={onClose}
            aria-label="Close"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 1L1 13M1 1L13 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        {/* Top Element (optional - e.g., image/illustration) */}
        {topElement && <div>{topElement}</div>}

        {/* Title */}
        <p className="whitespace-pre-line text-center text-[18px] font-medium leading-normal text-white md:text-[30px] md:leading-[38px]">
          {title}
        </p>

        {/* Buttons */}
        {(leftButtonText || rightButtonText) && (
          <div className="flex w-full flex-col items-stretch justify-center gap-2 sm:flex-row sm:gap-[10px]">
            {leftButtonText && (
              <CWButton
                title={leftButtonText}
                variant="dark"
                outline
                onClick={onLeftButtonClick}
                className="h-[50px] min-w-0 flex-1 border-[2.4px] border-white/10 text-base font-medium sm:h-[50px] sm:min-w-[150px] sm:flex-none sm:text-lg md:h-[60px] md:min-w-[200px] md:text-[24px]"
                parentClassname="flex-1 sm:flex-none sm:w-auto"
                {...leftButtonProps}
              />
            )}
            {rightButtonText && (
              <CWButton
                title={rightButtonText}
                variant="primary"
                onClick={onRightButtonClick}
                className="h-[50px] min-w-0 flex-1 border-[2.4px] border-white/10 text-base font-medium sm:h-[50px] sm:min-w-[150px] sm:flex-none sm:text-lg md:h-[60px] md:min-w-[200px] md:text-[24px]"
                parentClassname="flex-1 sm:flex-none sm:w-auto"
                {...rightButtonProps}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CWPopup;
